// Local implementation of https://github.com/danish1658/react-native-dropdown-select-list with own primitive style

import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import Animated, { cancelAnimation, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export interface SelectOption<T extends number | string> {
  key: T;
  value: string | React.ReactNode;
  disabled?: boolean;
}

interface SelectPickerProps<T extends number | string> {
  data: SelectOption<T>[];
  setSelected: (value: T) => void;
  placeholder?: string;
  defaultOption?: SelectOption<T>;
  notFoundText?: string;
  maxHeight?: number;
  onSelect?: () => void;
  save?: 'key' | 'value';
  dropdownShown?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
}

export function SelectPickerHtml<T extends number | string>({ ...props }: SelectPickerProps<T>) {
  const instanceKey = `${String(props.defaultOption?.key ?? '')}:${props.dropdownShown ? 'open' : 'closed'}:${String(
    props.maxHeight ?? 200,
  )}`;
  return <SelectPickerHtmlContent key={instanceKey} {...props} />;
}

function SelectPickerHtmlContent<T extends number | string>({
  data,
  setSelected,
  placeholder = 'Sélectionner une option',
  defaultOption,
  notFoundText = 'Aucun résultat',
  maxHeight = 200,
  save = 'key',
  dropdownShown = false,
  disabled = false,
  onSelect,
  searchable = false,
  searchPlaceholder = 'Rechercher...',
}: SelectPickerProps<T>) {
  const searchInputRef = useRef<TextInput>(null);
  const [dropdown, setDropdown] = useState<boolean>(dropdownShown);
  const [selectedKey, setSelectedKey] = useState<T | null>(defaultOption?.key ?? null);
  const [searchValue, setSearchValue] = useState<string>('');
  const dropdownHeight = useSharedValue(dropdownShown ? maxHeight : 0);
  const dropdownStyle = useAnimatedStyle(() => ({ maxHeight: dropdownHeight.get() }));

  useEffect(() => () => cancelAnimation(dropdownHeight), [dropdownHeight]);

  const filteredData = (() => {
    if (!searchable || !searchValue.trim()) {
      return data;
    }
    return data.filter((item) => {
      const searchText = typeof item.value === 'string' ? item.value : item.key.toString();
      return searchText.toLowerCase().includes(searchValue.toLowerCase());
    });
  })();

  const slidedown = () => {
    setDropdown(true);
    dropdownHeight.set(
      withTiming(maxHeight, { duration: 300 }, (finished) => {
        if (finished && searchable) {
          scheduleOnRN(focusSearchInput);
        }
      }),
    );
  };

  const focusSearchInput = () => {
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const finishSlideup = () => {
    setDropdown(false);
    setSearchValue('');
  };

  const slideup = () => {
    dropdownHeight.set(
      withTiming(0, { duration: 300 }, (finished) => {
        if (finished) {
          scheduleOnRN(finishSlideup);
        }
      }),
    );
  };

  const toggleDropdown = () => {
    if (disabled) return;

    if (!dropdown) {
      Keyboard.dismiss();
      slidedown();
    } else {
      slideup();
    }
  };

  const handleSelect = (item: SelectOption<T>) => {
    const valueToSave = save === 'value' ? (item.value as T) : item.key;
    setSelected(valueToSave);
    setSelectedKey(item.key);
    slideup();
    onSelect?.();
  };

  return (
    <View style={styles.container}>
      <Pressable
        onPress={toggleDropdown}
        disabled={disabled}
        style={({ pressed }) => [styles.selector, disabled && styles.disabledSelector, pressed && { opacity: 0.6 }]}
      >
        <Row
          alignItems="center"
          borderColor="border.secondary"
          borderRadius="radius.lg"
          borderWidth={1}
          flex={1}
          padding="spacing.general.x2"
          style={[styles.selectorRow, dropdown && styles.openSelector]}
        >
          <Text flex={1} fontSize={16} style={!selectedKey ? styles.placeholderText : undefined}>
            {defaultOption?.value || placeholder}
          </Text>

          <IconChevronRight
            strokeWidth={ICON_STROKE_WIDTH}
            color={disabled ? theme.colors['text.placeholder'] : theme.colors['text.primary']}
            size={24}
            style={[styles.chevron, dropdown && styles.chevronRotated]}
          />
        </Row>
      </Pressable>

      {dropdown ? (
        <Animated.View style={[dropdownStyle, styles.dropdown]}>
          {searchable ? (
            <View style={styles.searchContainer}>
              <TextInput
                ref={searchInputRef}
                style={styles.searchInput}
                placeholder={searchPlaceholder}
                placeholderTextColor={theme.colors['text.placeholder']}
                value={searchValue}
                onChangeText={setSearchValue}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
          ) : null}
          <ScrollView contentContainerStyle={styles.scrollContent} nestedScrollEnabled showsVerticalScrollIndicator>
            {filteredData.length >= 1 ? (
              filteredData.map((item) => {
                return (
                  <Pressable key={item.key} onPress={() => handleSelect(item)}>
                    <Row
                      paddingVertical="spacing.general.x1"
                      paddingHorizontal="spacing.general.x2"
                      backgroundColor={selectedKey === item.key ? 'surface.inverse.tertiary-neutral' : undefined}
                      borderRadius="radius.xl"
                      marginHorizontal="spacing.general.x2"
                      marginVertical="spacing.general.x1"
                    >
                      <Text style={[styles.optionText, selectedKey === item.key && styles.selectedOptionText]}>
                        {item.value}
                      </Text>
                    </Row>
                  </Pressable>
                );
              })
            ) : (
              <View style={styles.option}>
                <Text style={styles.optionText}>{notFoundText}</Text>
              </View>
            )}
          </ScrollView>
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1,
  },
  selector: {
    width: '100%',
  },
  selectorRow: {
    height: 48,
    backgroundColor: 'white',
  },
  openSelector: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  disabledSelector: {
    opacity: 0.6,
  },
  placeholderText: {
    color: theme.colors['text.placeholder'],
  },
  selectedText: {
    color: theme.colors['text.primary'],
  },
  chevron: {
    transform: [{ rotate: '90deg' }],
  },
  chevronRotated: {
    transform: [{ rotate: '270deg' }],
  },
  dropdown: {
    borderWidth: 1,
    borderColor: theme.colors['border.secondary'],
    borderRadius: 8,
    marginTop: 10,
    overflow: 'hidden',
    backgroundColor: 'white',
    boxShadow: '0px 2px 3.84px rgba(0, 0, 0, 0.25)',
  },
  searchContainer: {
    marginTop: 10,
    paddingHorizontal: 8,
    borderBottomColor: theme.colors['border.secondary'],
    backgroundColor: 'white',
  },
  searchInput: {
    height: 40,
    paddingHorizontal: 16,
    fontSize: 16,
    color: theme.colors['text.primary'],
    backgroundColor: 'white',
  },
  scrollContent: {
    paddingVertical: 8,
  },
  option: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionText: {
    fontSize: 16,
    color: theme.colors['text.primary'],
  },
  selectedOptionText: {
    fontWeight: '700',
    color: theme.colors['text.placeholder'],
  },
  disabledOption: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    opacity: 0.6,
  },
  disabledText: {
    fontSize: 16,
    color: theme.colors['text.placeholder'],
  },
});
